import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Card, Button, Alert, Descriptions, Typography, Space, Modal, Tag, Select, Spin, Segmented, Tooltip } from 'antd';
import { SwapOutlined } from '@ant-design/icons';
import KlineChart from './KlineChart';
import api from '../api';
import { useI18n } from '../i18n';

// 走势图时间范围（label 为 i18n key，渲染时 t() 翻译；"全部"= 池子创建以来的全部日线）
const CHART_RANGES = [
  { label: '24小时', value: '24h' },
  { label: '7天', value: '7d' },
  { label: '30天', value: '30d' },
  { label: '90天', value: '90d' },
  { label: '全部', value: 'all' }
];

// 与 backend/config/constants.js 保持一致
const AMOUNTS = [5, 10, 20, 30, 50, 66, 80, 100];
const MIN_BNB_FOR_GAS = 0.002;
const MIN_BNB_FOR_TOPUP = 0.0001; // 低于此值连"用 USDT 换 BNB"都发不出去
const GAS_TOPUP_USDT = 2; // 自动补 gas 消耗的 USDT

// swap 方向常量：buy = USDT→USGNAA，sell = USGNAA→USDT
const DIRECTION_META = {
  buy: { inputSymbol: 'USDT', outputSymbol: 'USGNAA' },
  sell: { inputSymbol: 'USGNAA', outputSymbol: 'USDT' }
};

// 是否需要执行前自动补 gas
const needsTopUp = (w) => parseFloat(w.bnbBalance) < MIN_BNB_FOR_GAS;

// 该钱包在指定方向下的输入 token 余额
const inputBalanceOf = (w, dir) =>
  dir === 'sell' ? parseFloat(w.usgnaaBalance ?? 0) : parseFloat(w.usdtBalance);

// 该方向下可用于 swap 的输入余额（买入方向需补 gas 时预留 2 USDT；补 gas 固定花 USDT）
const usableInput = (w, dir) =>
  dir === 'sell'
    ? inputBalanceOf(w, 'sell')
    : inputBalanceOf(w, 'buy') - (needsTopUp(w) ? GAS_TOPUP_USDT : 0);

// 钱包是否具备执行条件；不满足时返回禁用原因的 i18n key（渲染处 t() 翻译）
const ineligibleReason = (w, dir) => {
  if (!w.imported) return '私钥未导入';
  if (parseFloat(w.bnbBalance) < MIN_BNB_FOR_TOPUP) return 'gas不足需分发';
  if (dir === 'sell' && needsTopUp(w) && parseFloat(w.usdtBalance) < GAS_TOPUP_USDT) {
    return '补gas所需USDT不足';
  }
  if (usableInput(w, dir) < AMOUNTS[0]) return `${DIRECTION_META[dir].inputSymbol}不足`;
  return null;
};

// 在钱包可用余额内随机选一个金额档位
const randomAmountFor = (w, dir) => {
  const affordable = AMOUNTS.filter((a) => a <= usableInput(w, dir));
  return affordable.length ? affordable[Math.floor(Math.random() * affordable.length)] : null;
};

// 行情卡片 + Swap：主体为 USGNAA/USDT 价格走势图；
// 右上角 Swap 按钮弹窗确认（钱包随机预选、可下拉更换，gas 不足的禁用），确认后执行
const SwapForm = ({ wallets, onSwapDone }) => {
  const { t, ts } = useI18n();
  const [modalOpen, setModalOpen] = useState(false);
  // 走势图状态
  const [chartRange, setChartRange] = useState('24h');
  const [chartPoints, setChartPoints] = useState([]);
  const [chartLoading, setChartLoading] = useState(false);
  const [chartError, setChartError] = useState('');
  // 行情最近成功刷新时间
  const [lastRefreshed, setLastRefreshed] = useState(null);
  // 池内现价（基于池子储备比值，1 USGNAA = ? USDT）
  const [spotPrice, setSpotPrice] = useState(null);
  // 计价方向：usgnaa_usdt = 1 USGNAA 值多少 USDT（接口原始方向）；usdt_usgnaa = 倒数方向
  const [pairDirection, setPairDirection] = useState('usgnaa_usdt');
  const chartRequestIdRef = useRef(0);
  const [selAddress, setSelAddress] = useState(undefined);
  const [selAmount, setSelAmount] = useState(undefined);
  // swap 方向：buy = USDT→USGNAA（默认），sell = USGNAA→USDT
  const [swapDirection, setSwapDirection] = useState('buy');
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  // 金额快速切换时丢弃过期报价响应
  const quoteRequestIdRef = useRef(0);

  const selWallet = wallets.find((w) => w.address === selAddress);

  // 加载价格走势（切换范围时丢弃过期响应）；silent=true 为后台自动刷新，不闪 loading、失败保留旧数据
  const loadChart = useCallback(async (range, silent = false) => {
    const requestId = ++chartRequestIdRef.current;
    if (!silent) {
      setChartLoading(true);
      setChartError('');
    }
    try {
      const res = await api.get('/market/ohlcv', { params: { range } });
      if (requestId !== chartRequestIdRef.current) return;
      setChartPoints(res.data.data);
      setChartError('');
      setLastRefreshed(new Date());
    } catch (err) {
      if (requestId !== chartRequestIdRef.current) return;
      if (!silent) {
        setChartPoints([]);
        setChartError(err.response?.data?.error || err.message);
      }
    } finally {
      if (!silent && requestId === chartRequestIdRef.current) setChartLoading(false);
    }
  }, []);

  // 池内现价：失败静默（保留上次值），随图表同节奏每分钟刷新
  const loadSpot = useCallback(async () => {
    try {
      const res = await api.get('/market/spot');
      setSpotPrice(res.data.data.price);
    } catch {
      // 静默失败，下个周期重试
    }
  }, []);

  useEffect(() => {
    loadChart(chartRange);
    loadSpot();
    // 每分钟静默自动刷新当前档位与池内现价
    const timer = setInterval(() => {
      loadChart(chartRange, true);
      loadSpot();
    }, 60 * 1000);
    return () => clearInterval(timer);
  }, [chartRange, loadChart, loadSpot]);

  // 按计价方向换算 K 线：倒数方向时 open/close 取倒数，高低互换
  const isInverse = pairDirection === 'usdt_usgnaa';
  const displayPoints = isInverse
    ? chartPoints
        .filter((p) => [p.open, p.high, p.low, p.close].every((v) => typeof v === 'number' && v > 0))
        .map((p) => ({
          timestamp: p.timestamp,
          open: 1 / p.open,
          high: 1 / p.low,
          low: 1 / p.high,
          close: 1 / p.close,
          // 成交量为 USD 计价，与计价方向无关
          volume: p.volume
        }))
    : chartPoints;

  // 最新价与区间涨跌幅（按收盘价计算）；?? null 兜住字段缺失（如后端未重启仍返回旧结构）
  const latestPrice = displayPoints.length ? displayPoints[displayPoints.length - 1].close ?? null : null;
  const firstClose = displayPoints.length > 1 ? displayPoints[0].close ?? null : null;
  const changePercent =
    latestPrice != null && firstClose != null ? ((latestPrice - firstClose) / firstClose) * 100 : null;

  const baseSymbol = isInverse ? 'USDT' : 'USGNAA';
  const quoteSymbol = isInverse ? 'USGNAA' : 'USDT';

  const fetchQuote = async (amount, direction = swapDirection) => {
    if (!amount) {
      setQuote(null);
      return;
    }
    const requestId = ++quoteRequestIdRef.current;
    setQuoteLoading(true);
    try {
      const res = await api.post('/swap/quote', { amount, direction });
      if (requestId !== quoteRequestIdRef.current) return;
      setQuote(res.data.data);
    } catch (err) {
      if (requestId !== quoteRequestIdRef.current) return;
      setQuote(null);
      setError(ts(err.response?.data?.error || err.message));
    } finally {
      if (requestId === quoteRequestIdRef.current) setQuoteLoading(false);
    }
  };

  // 在指定方向下随机预选钱包与金额并拉取报价
  const randomizeSelection = (direction) => {
    const eligible = wallets.filter((w) => !ineligibleReason(w, direction));
    const wallet = eligible.length ? eligible[Math.floor(Math.random() * eligible.length)] : null;
    const amount = wallet ? randomAmountFor(wallet, direction) : undefined;
    setSelAddress(wallet ? wallet.address : undefined);
    setSelAmount(amount ?? undefined);
    setQuote(null);
    if (amount) fetchQuote(amount, direction);
  };

  // 点击 Swap：直接开弹窗，随机预选一个可用钱包与金额
  const handleOpen = () => {
    setResult(null);
    setError('');
    setModalOpen(true);
    randomizeSelection(swapDirection);
  };

  // 切换方向：钱包可用性与金额上限都会变化，重新随机
  const handleDirectionChange = (direction) => {
    setSwapDirection(direction);
    randomizeSelection(direction);
  };

  // 换钱包：金额在新钱包余额内重新随机
  const handleWalletChange = (address) => {
    setSelAddress(address);
    const w = wallets.find((x) => x.address === address);
    const amount = w ? randomAmountFor(w, swapDirection) : undefined;
    setSelAmount(amount ?? undefined);
    fetchQuote(amount);
  };

  const handleAmountChange = (amount) => {
    setSelAmount(amount);
    fetchQuote(amount);
  };

  const handleConfirm = async () => {
    setExecuting(true);
    setError('');
    try {
      const res = await api.post('/swap/execute', {
        address: selAddress,
        amount: selAmount,
        direction: swapDirection
      });
      setResult(res.data.data);
      setModalOpen(false);
      onSwapDone();
    } catch (err) {
      setError(ts(err.response?.data?.error || err.message));
      setModalOpen(false);
    } finally {
      setExecuting(false);
    }
  };

  const { inputSymbol, outputSymbol } = DIRECTION_META[swapDirection];

  // 禁用原因 key → 本地化文案（含带 token 符号的动态原因）
  const reasonText = (reason) => {
    if (!reason) return '';
    if (reason.endsWith('不足') && reason !== 'gas不足需分发') {
      return t('{sym}不足', { sym: reason.replace('不足', '') });
    }
    return t(reason);
  };
  const walletOptions = wallets.map((w) => {
    const reason = ineligibleReason(w, swapDirection);
    const topUpNote = !reason && needsTopUp(w) ? t('·自动补gas') : '';
    return {
      value: w.address,
      disabled: !!reason,
      label: `${w.label}  ·  ${inputBalanceOf(w, swapDirection).toFixed(2)} ${inputSymbol}${reason ? `（${reasonText(reason)}）` : topUpNote}`
    };
  });

  const amountOptions = AMOUNTS.map((a) => ({
    value: a,
    label: `${a} ${inputSymbol}`,
    disabled: !selWallet || a > usableInput(selWallet, swapDirection)
  }));

  const noneEligible = wallets.length > 0 && wallets.every((w) => ineligibleReason(w, swapDirection));

  return (
    <Card
      title={
        <Space>
          <span>{t('行情')}</span>
          <Segmented
            size="small"
            value={pairDirection}
            onChange={setPairDirection}
            options={[
              { label: 'USGNAA/USDT', value: 'usgnaa_usdt' },
              { label: 'USDT/USGNAA', value: 'usdt_usgnaa' }
            ]}
          />
          {spotPrice != null && (
            <Tooltip title={t('池内现价：基于池子储备比值实时计算（getReserves），不含手续费与滑点，每分钟刷新')}>
              <Tag color="blue" style={{ fontWeight: 'normal' }}>
                {t('池内现价')} {(isInverse ? 1 / spotPrice : spotPrice).toFixed(6)} {isInverse ? 'USGNAA' : 'USDT'}
              </Tag>
            </Tooltip>
          )}
        </Space>
      }
      extra={
        <Space>
          <Segmented
            value={chartRange}
            onChange={setChartRange}
            options={CHART_RANGES.map((r) => ({ ...r, label: t(r.label) }))}
          />
          <Button
            type="primary"
            icon={<SwapOutlined />}
            onClick={handleOpen}
            disabled={wallets.length === 0}
          >
            Swap
          </Button>
        </Space>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {latestPrice != null && (
          <Space size="middle" align="baseline" wrap>
            <Typography.Title level={3} style={{ margin: 0 }}>
              {latestPrice.toFixed(6)}{' '}
              <Typography.Text type="secondary" style={{ fontSize: 14 }}>
                {quoteSymbol}
              </Typography.Text>
            </Typography.Title>
            {changePercent != null && (
              <Typography.Text type={changePercent >= 0 ? 'success' : 'danger'}>
                {changePercent >= 0 ? '+' : ''}
                {changePercent.toFixed(2)}%（{t(CHART_RANGES.find((r) => r.value === chartRange)?.label)}）
              </Typography.Text>
            )}
            {/* 同时展示反方向最新价 */}
            <Typography.Text type="secondary">
              1 {quoteSymbol} ≈ {(1 / latestPrice).toFixed(6)} {baseSymbol}
            </Typography.Text>
            {lastRefreshed && (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {t('更新于 {time}（每分钟自动刷新）', { time: lastRefreshed.toLocaleTimeString() })}
              </Typography.Text>
            )}
          </Space>
        )}
        {chartError && <Alert type="warning" showIcon message={chartError} />}
        <Spin spinning={chartLoading}>
          <KlineChart points={displayPoints} height={300} />
        </Spin>
        {error && <Alert type="error" showIcon message={t('Swap 失败')} description={error} />}
        {result && (
          <Alert
            type="success"
            showIcon
            message={t('Swap 成功')}
            description={
              <Descriptions column={1} size="small" style={{ marginTop: 8 }}>
                <Descriptions.Item label={t('钱包_单')}>
                  {result.label}（{result.address.slice(0, 8)}...{result.address.slice(-6)}）
                </Descriptions.Item>
                <Descriptions.Item label={t('支出')}>
                  {result.amount} {result.inputSymbol ?? 'USDT'}
                </Descriptions.Item>
                <Descriptions.Item label={t('换得')}>
                  {result.amountOut ?? '?'} {result.outputSymbol ?? 'USGNAA'}
                </Descriptions.Item>
                {result.gasTopUp && (
                  <Descriptions.Item label={t('自动补 gas')}>
                    {result.gasTopUp.usdtSpent} USDT → ≈{Number(result.gasTopUp.bnbGained).toFixed(5)} BNB（
                    <a href={`https://bscscan.com/tx/${result.gasTopUp.txHash}`} target="_blank" rel="noreferrer">
                      {result.gasTopUp.txHash.slice(0, 12)}...
                    </a>
                    ）
                  </Descriptions.Item>
                )}
                <Descriptions.Item label={t('交易')}>
                  <a href={result.bscscanUrl} target="_blank" rel="noreferrer">
                    {result.txHash.slice(0, 18)}... {t('在 BscScan 查看')}
                  </a>
                </Descriptions.Item>
              </Descriptions>
            }
          />
        )}
      </Space>

      {/* 报价确认弹窗 */}
      <Modal
        title={t('确认 Swap')}
        open={modalOpen}
        onOk={handleConfirm}
        onCancel={() => setModalOpen(false)}
        okText={t('确认执行')}
        cancelText={t('取消')}
        confirmLoading={executing}
        okButtonProps={{ disabled: !selAddress || !selAmount || !quote || quoteLoading }}
        maskClosable={false}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%', marginTop: 12 }}>
          <Segmented
            block
            value={swapDirection}
            onChange={handleDirectionChange}
            options={[
              { label: t('USDT → USGNAA（买入）'), value: 'buy' },
              { label: t('USGNAA → USDT（卖出）'), value: 'sell' }
            ]}
          />
          {noneEligible && (
            <Alert
              type="warning"
              showIcon
              message={t('当前没有可执行的钱包：需要私钥已导入、{sym} 足额且 BNB ≥ {min}。可先点钱包列表的"分发 gas"，或外部充值 BNB', { sym: inputSymbol, min: MIN_BNB_FOR_TOPUP })}
            />
          )}
          {selWallet && needsTopUp(selWallet) && (
            <Alert
              type="info"
              showIcon
              message={t('该钱包 BNB 低于 {min}，执行前会自动先用 {usdt} USDT 换 BNB 补 gas', { min: MIN_BNB_FOR_GAS, usdt: GAS_TOPUP_USDT })}
            />
          )}
          <div>
            <Typography.Text type="secondary">{t('钱包_单')}</Typography.Text>
            <Select
              style={{ width: '100%', marginTop: 4 }}
              placeholder={t('选择钱包')}
              value={selAddress}
              onChange={handleWalletChange}
              options={walletOptions}
            />
          </div>
          <div>
            <Typography.Text type="secondary">{t('支出金额')}</Typography.Text>
            <Select
              style={{ width: '100%', marginTop: 4 }}
              placeholder={t('选择金额档位')}
              value={selAmount}
              onChange={handleAmountChange}
              options={amountOptions}
              disabled={!selWallet}
            />
          </div>

          <Spin spinning={quoteLoading}>
            {quote ? (
              <Descriptions column={1} size="small" bordered>
                <Descriptions.Item label={t('预计换得')}>
                  <Typography.Text strong>
                    {Number(quote.expectedOut).toFixed(6)} {quote.outputSymbol ?? outputSymbol}
                  </Typography.Text>
                </Descriptions.Item>
                <Descriptions.Item label={t('最少到账（滑点 {pct}%）', { pct: quote.slippagePercent })}>
                  {Number(quote.minReceived).toFixed(6)} {quote.outputSymbol ?? outputSymbol}
                </Descriptions.Item>
                <Descriptions.Item label={t('执行汇率')}>
                  1 USGNAA ≈ {quote.usdtPerUsgnaa ?? '?'} USDT
                </Descriptions.Item>
                {quote.pool && (
                  <>
                    <Descriptions.Item label={t('池内现价')}>
                      1 USGNAA ≈ {quote.pool.midPriceUsdtPerUsgnaa} USDT
                    </Descriptions.Item>
                    <Descriptions.Item label={t('池子流动性')}>
                      {Number(quote.pool.usdtReserve).toFixed(2)} USDT / {Number(quote.pool.usgnaaReserve).toFixed(2)} USGNAA
                    </Descriptions.Item>
                  </>
                )}
                <Descriptions.Item label={t('价格影响')}>
                  {quote.priceImpactPercent === null || quote.priceImpactPercent === undefined
                    ? t('未知')
                    : quote.priceImpactPercent < 0.01
                      ? '<0.01%'
                      : `${quote.priceImpactPercent.toFixed(2)}%`}
                </Descriptions.Item>
                <Descriptions.Item label={t('交易手续费（0.25%）')}>
                  ≈ {quote.tradingFee ?? quote.tradingFeeUsdt} {quote.inputSymbol ?? inputSymbol}
                </Descriptions.Item>
                <Descriptions.Item label={t('路径')}>{quote.route}</Descriptions.Item>
              </Descriptions>
            ) : (
              <Typography.Text type="secondary">
                {selAmount ? t('报价加载中...') : t('选择钱包与金额后展示报价')}
              </Typography.Text>
            )}
          </Spin>

          {selWallet && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              <Tag color="blue">{selWallet.label}</Tag>
              {t('余额 {bal} {sym} · 确认执行时将按最新链上报价重新计算滑点保护下限，实际到账以链上成交为准', { bal: inputBalanceOf(selWallet, swapDirection).toFixed(2), sym: inputSymbol })}
            </Typography.Text>
          )}
        </Space>
      </Modal>
    </Card>
  );
};

export default SwapForm;
